// src/pages/SeriesDetail.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSeriesById, getAllSeries } from "../utils/seriesUtils";
import VideoPlayer from "../components/VideoPlayer";
import { getCurrentUser, toggleFavorite, isFavorite } from "../utils/auth";
import { handleImgError } from "../utils/thumbnailUtils";
import { PageSEO, buildTVSeriesSchema } from "../hooks/useSEO";
import "../styles/styles.css";

const SeriesDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [serie, setSerie] = useState(null);
  const [allSeries, setAllSeries] = useState([]);
  const [favorite, setFavorite] = useState(false);
  const [user] = useState(() => getCurrentUser());
  const [showFullSynopsis, setShowFullSynopsis] = useState(false);

  useEffect(() => {
    const found = getSeriesById(id);
    if (!found) {
      navigate("/");
    } else {
      setSerie(found);
      setAllSeries(getAllSeries());
      if (user && user.role === "user") {
        setFavorite(isFavorite(user.email, found.id));
      }
      window.scrollTo(0, 0);
    }
  }, [id, user, navigate]);

  const handleFavorite = () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "user") return;
    const updated = toggleFavorite(user.email, serie.id);
    setFavorite(updated.includes(serie.id));
  };

  if (!serie) return null;

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  // Series relacionadas: mismo género, distinto id
  const related = allSeries
    .filter(s => s.id !== serie.id && s.genre.some(g => serie.genre.includes(g)))
    .slice(0, 4);

  const synopsis = showFullSynopsis
    ? (serie.longSynopsis || serie.synopsis)
    : (serie.synopsis);

  const statusColor = {
    "Completa": "#4ade80",
    "En producción": "#38bdf8",
    "Temporada 2 anunciada": "#a78bfa",
  }[serie.status] || "#94a3b8";

  return (
    <div className="detail-page">
      <PageSEO
        title={serie.title}
        description={serie.synopsis}
        image={serie.thumbnail}
        url={`/series/${serie.id}`}
        type="video.tv_show"
        schema={buildTVSeriesSchema({
          title: serie.title,
          synopsis: serie.synopsis,
          thumbnail: serie.thumbnail,
          releaseDate: serie.releaseDate,
          genres: serie.genre,
          episodes: serie.episodes,
          url: `/series/${serie.id}`,
        })}
      />

      {/* Hero con thumbnail de fondo */}
      <div className="detail-hero" style={{ backgroundImage: `url(${serie.thumbnail})` }}>
        <div className="detail-hero-overlay" />
        <button className="detail-back-btn" onClick={goBack}>← Volver</button>
        <div className="detail-hero-content">
          <div className="detail-badges-row">
            <span className="detail-type-badge">SERIE IA</span>
            <span className="detail-ai-badge" title="Contenido generado íntegramente con inteligencia artificial">🤖 AI-Generated</span>
            {serie.ageRating && <span className="detail-age-badge">{serie.ageRating}</span>}
            {serie.quality && <span className="detail-quality-badge">{serie.quality}</span>}
            {serie.status && (
              <span className="detail-status-badge" style={{ borderColor: statusColor, color: statusColor }}>
                {serie.status}
              </span>
            )}
          </div>
          <h1 className="detail-title-hero">{serie.title}</h1>
          <div className="detail-meta-row">
            <span>{serie.episodes} episodios</span>
            <span>·</span>
            <span>{serie.durationPerEpisode}/ep</span>
            <span>·</span>
            <span>{new Date(serie.releaseDate).getFullYear()}</span>
            <span>·</span>
            <span>{serie.genre.join(", ")}</span>
          </div>
          <div className="detail-hero-actions">
            <button className="btn-primary btn-play-big" onClick={() => document.getElementById("player-section").scrollIntoView({ behavior: "smooth" })}>
              ▶ Ver ahora
            </button>
            <button
              className={`btn-outline-icon ${favorite ? "favorited" : ""}`}
              onClick={handleFavorite}
              title={favorite ? "Quitar de Mi Lista" : "Añadir a Mi Lista"}
            >
              {favorite ? "✓ En Mi Lista" : "+ Mi Lista"}
            </button>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="detail-body">
        <div className="detail-main-grid">

          {/* Columna izquierda: sinopsis + ficha técnica */}
          <div className="detail-left">
            <div className="detail-synopsis-block">
              <p className="detail-synopsis-text">{synopsis}</p>
              {serie.longSynopsis && (
                <button
                  className="detail-synopsis-toggle"
                  onClick={() => setShowFullSynopsis(!showFullSynopsis)}
                >
                  {showFullSynopsis ? "Ver menos ▲" : "Leer más ▼"}
                </button>
              )}
            </div>

            {/* Tags */}
            {serie.tags && (
              <div className="detail-tags">
                {serie.tags.map(tag => (
                  <span key={tag} className="detail-tag">#{tag}</span>
                ))}
              </div>
            )}

            {/* Ficha técnica */}
            <div className="detail-tech-sheet">
              <h3 className="detail-section-title">Ficha técnica</h3>
              <div className="detail-tech-grid">
                {serie.language && (
                  <div className="detail-tech-item">
                    <span className="detail-tech-label">Idioma</span>
                    <span className="detail-tech-value">{serie.language}</span>
                  </div>
                )}
                {serie.subtitles && serie.subtitles.length > 0 && (
                  <div className="detail-tech-item">
                    <span className="detail-tech-label">Subtítulos</span>
                    <span className="detail-tech-value">{serie.subtitles.join(", ")}</span>
                  </div>
                )}
                <div className="detail-tech-item">
                  <span className="detail-tech-label">Episodios</span>
                  <span className="detail-tech-value">{serie.episodes}</span>
                </div>
                <div className="detail-tech-item">
                  <span className="detail-tech-label">Duración/ep</span>
                  <span className="detail-tech-value">{serie.durationPerEpisode}</span>
                </div>
                <div className="detail-tech-item">
                  <span className="detail-tech-label">Estreno</span>
                  <span className="detail-tech-value">{serie.releaseDate}</span>
                </div>
                {serie.director && (
                  <div className="detail-tech-item">
                    <span className="detail-tech-label">Dirección IA</span>
                    <span className="detail-tech-value">{serie.director}</span>
                  </div>
                )}
              </div>

              {serie.cast && serie.cast.length > 0 && (
                <div className="detail-cast">
                  <span className="detail-tech-label">Voces IA</span>
                  <div className="detail-cast-list">
                    {serie.cast.map(c => (
                      <span key={c} className="detail-cast-chip">{c}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Columna derecha: thumbnail */}
          <div className="detail-right">
            <img
              src={serie.thumbnail}
              alt={serie.title}
              className="detail-thumbnail-side"
              onError={(e) => handleImgError(e, serie.title, 800, 450)}
            />
          </div>
        </div>

        {/* Player */}
        <div id="player-section" className="player-wrapper">
          <h3 className="detail-section-title">▶ Reproducir</h3>
          <VideoPlayer videoUrl={serie.videoUrl} user={user} />
        </div>

        {/* Contenido relacionado */}
        {related.length > 0 && (
          <div className="detail-related">
            <h3 className="detail-section-title">También te puede gustar</h3>
            <div className="detail-related-grid">
              {related.map(s => (
                <div
                  key={s.id}
                  className="detail-related-card"
                  onClick={() => navigate(`/series/${s.id}`)}
                >
                  <div className="detail-related-img">
                    <img
                      src={s.thumbnail}
                      alt={s.title}
                      onError={(e) => handleImgError(e, s.title, 400, 225)}
                    />
                    <div className="detail-related-overlay">▶</div>
                  </div>
                  <div className="detail-related-info">
                    <p className="detail-related-title">{s.title}</p>
                    <p className="detail-related-meta">{s.episodes} ep · {s.genre[0]}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeriesDetail;
